import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useNavigate } from 'react-router-dom'
import { getRegisterStatus } from '../api/auth'
import loginHero from '../assets/login-hero.png'
import BrandLogo from '../components/BrandLogo'
import { useAuth } from '../hooks/useAuth'
import { homePath, resolveOrgSlug } from '../utils/org-slug'
import { waitForApiReady } from '../utils/waitForApi'

function startOAuthSignIn(provider: 'google' | 'github' | 'microsoft'): void {
  // No org, ever. The org is an output of authentication: the callback resolves
  // which orgs this identity opens once the provider has verified it, and asks
  // only if there is genuinely more than one.
  window.location.href = `/auth/oauth/${provider}/start`
}

export default function LoginPage() {
  const [emailOrUsername, setEmailOrUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [registrationOpen, setRegistrationOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [heroFailed, setHeroFailed] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const isEmail = emailOrUsername.includes('@')

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const ready = await waitForApiReady()
      if (cancelled || !ready) return

      try {
        const registerStatus = await getRegisterStatus()
        if (cancelled) return
        setRegistrationOpen(registerStatus.registrationOpen)
      } catch {
        // Ignore — registration link is optional
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate email format if @ is entered
    if (isEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(emailOrUsername)) {
        setError('Please enter a valid email address')
        return
      }
    }

    setIsLoading(true)

    try {
      const response = await login(emailOrUsername, password)
      if (response.requiresOrgSelection) {
        // Choosing an org is a post-authentication step and lives on its own
        // route, so it can never render on this screen.
        navigate('/select-org', {
          replace: true,
          state: { selectToken: response.selectToken ?? null },
        })
        return
      }
      // Bare entry hard-navigates inside useAuth (window.location to
      // /{slug}/dashboard) — nothing left to do here.
      if (resolveOrgSlug() === undefined && response.orgSlug) {
        toast.success('Welcome back!')
        return
      }
      toast.success('Welcome back!')
      navigate(homePath())
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen app-sky lg:grid lg:grid-cols-[minmax(0,1.45fr)_minmax(360px,0.8fr)]">
      <div className="relative hidden lg:flex items-center justify-center overflow-hidden p-6 xl:p-10">
        {!heroFailed && (
          <img
            src={loginHero}
            alt="ContextHarbor — Build AI applications, not knowledge infrastructure."
            className="max-h-[calc(100vh-3rem)] w-full object-contain object-center"
            onError={() => setHeroFailed(true)}
          />
        )}
      </div>

      <div className="flex flex-col justify-center px-4 py-10 sm:px-8">
        {!heroFailed && (
          <div className="mb-8 lg:hidden">
            <img
              src={loginHero}
              alt="ContextHarbor — Build AI applications, not knowledge infrastructure."
              className="w-full max-w-xl mx-auto rounded-xl border border-harbor-border"
              onError={() => setHeroFailed(true)}
            />
          </div>
        )}

        <div className="w-full max-w-md mx-auto">
          <div className="text-center mb-8">
            <BrandLogo size={64} src="/icon-256.png" className="mx-auto rounded-2xl mb-4" />
            <h1 className="text-2xl font-bold text-harbor-text">Welcome back</h1>
            <p className="text-harbor-text-secondary mt-2">Sign in to your ContextHarbor account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label
                htmlFor="emailOrUsername"
                className="block text-sm font-medium text-harbor-text-secondary mb-1"
              >
                Username or Email
              </label>
              <input
                id="emailOrUsername"
                name="emailOrUsername"
                type="text"
                value={emailOrUsername}
                onChange={(e) => {
                  setEmailOrUsername(e.target.value)
                  setError('')
                }}
                required
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                className="w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="username or you@example.com"
              />
              {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-harbor-text-secondary"
                >
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-emerald-400 hover:text-emerald-300 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 pr-12 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-harbor-text-muted hover:text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 rounded-r-lg"
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                      <line x1="2" y1="2" x2="22" y2="22" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-harbor-border" />
            <span className="text-xs uppercase tracking-wider text-harbor-text-muted">or</span>
            <div className="h-px flex-1 bg-harbor-border" />
          </div>

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => startOAuthSignIn('google')}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-harbor-surface border border-harbor-border text-harbor-text font-medium rounded-lg hover:bg-harbor-surface-raised focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.95v2.33A9 9 0 0 0 9 18Z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.95A9 9 0 0 0 0 9c0 1.45.35 2.83.95 4.05l3.02-2.33Z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.59-2.59C13.46.9 11.43 0 9 0A9 9 0 0 0 .95 4.95l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
                />
              </svg>
              Sign in with Google
            </button>
            <button
              type="button"
              onClick={() => startOAuthSignIn('github')}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-harbor-surface border border-harbor-border text-harbor-text font-medium rounded-lg hover:bg-harbor-surface-raised focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset transition-colors"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
              </svg>
              Sign in with GitHub
            </button>
            <button
              type="button"
              onClick={() => startOAuthSignIn('microsoft')}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-harbor-surface border border-harbor-border text-harbor-text font-medium rounded-lg hover:bg-harbor-surface-raised focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#F25022" d="M1 1h7.5v7.5H1z" />
                <path fill="#7FBA00" d="M9.5 1H17v7.5H9.5z" />
                <path fill="#00A4EF" d="M1 9.5h7.5V17H1z" />
                <path fill="#FFB900" d="M9.5 9.5H17V17H9.5z" />
              </svg>
              Sign in with Microsoft
            </button>
          </div>

          {registrationOpen && (
            <p className="mt-6 text-center text-sm text-harbor-text-secondary">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-emerald-400 hover:text-emerald-300 font-medium">
                Create account
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
